/*
 * IMPORTS
 */
import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  Text,
  useBreakpointValue,
  useStyleConfig,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import _ from "underscore";
import Stringg from "string";
import { DataGrid } from "@mui/x-data-grid";

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import SmsReadFailedQuery from "./__query__/index.sms.read.failed.query";

/*
 * OBJECTS
 */
function Index({ title }) {
  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );

  React.useEffect(() => {
    return () => {
      _setFilterDebounced.cancel?.();
    };
  }, [_setFilterDebounced]);

  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;

  const _QuerySmsReadFailed = useQuery(SmsReadFailedQuery, {
    variables: {
      startDate: dateRange?.selection?.startDate,
      endDate: dateRange?.selection?.endDate,
      filter: _appliedFilter,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
    notifyOnNetworkStatusChange: true,
  });

  const _styles = useStyleConfig("Card");
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });
  const _tableHeaderHeightRef = React.useRef(0);

  // Handle refresh with loading state
  const _HandleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await _QuerySmsReadFailed.refetch();
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  };

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 80,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "error",
        headerName: "Error Code",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: "count",
        headerName: "Count",
        align: "center",
        headerAlign: "center",
        width: 120,
        editable: false,
        sortable: true,
        disableColumnMenu: true,
      },
    ],
    [],
  );

  // Define rows
  const rows = React.useMemo(() => {
    if (!_QuerySmsReadFailed?.data?.SmsReadFailed?.data) return [];

    return _QuerySmsReadFailed.data.SmsReadFailed.data.map((item, index) => {
      const _error = [];

      if (item && item?.dlr) {
        const _dlrError = Stringg(item?.dlr ?? "").between(" err:", "text")?.s;
        !_dlrError?.includes("001") && _error.push(_dlrError);
      }

      if (item && item?.error) {
        _error.push(item.error);
      }

      return {
        id: `error-${index}-${item?._count?._all || 0}`,
        sno: `${index + 1}.`,
        error:
          0 === _error?.length ? "-" : _.without(_error, "NONE")?.join(", "),
        count: item?._count?._all ?? 0,
      };
    });
  }, [_QuerySmsReadFailed?.data?.SmsReadFailed?.data]);

  // Return component.
  return (
    <Box
      __css={_styles}
      display="flex"
      flexDirection="column"
      w="100%"
      p={_isCurrentViewMobile ? "12px" : "22px"}
      gap={_isCurrentViewMobile ? "12px" : "22px"}
      fontSize={["clamp(12px, 1.5vw, 15px)"]}
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.17)"
    >
      <Flex justify="space-between" alignItems="center" width="100%">
        <Text
          color="rgba(43,54,116,1)"
          fontWeight="700"
          fontSize={{ base: "sm", md: "md" }}
          textAlign="left"
        >
          {title ?? "ERROR CODE"}
        </Text>
        <Flex alignItems="center" gap="8px">
          <DateRangePicker
            startDate={dateRange?.selection?.startDate}
            endDate={dateRange?.selection?.endDate}
            w="100%"
            fontSize="13px"
            setSelectDate={setDateRange}
          />
          <Button
            onClick={_HandleRefresh}
            isLoading={isRefreshing || _QuerySmsReadFailed.loading}
            size="sm"
            height="32px"
            px="12px"
            bg="none"
            _hover={{ bg: "gray.100" }}
            _active={{ bg: "gray.200" }}
          >
            <RepeatIcon />
          </Button>
        </Flex>
      </Flex>
      <Box width="100%" height="456px">
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={50}
          hideFooter
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={_setFilterDebounced}
          sx={{
            "& .MuiDataGrid-main": {
              borderBottomLeftRadius: "12px",
              borderBottomRightRadius: "12px",
              overflow: "hidden",
            },
          }}
          slots={{
            loadingOverlay: () => (
              <TableSpinner
                isLoading={true}
                isEmpty={false}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noResultsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noRowsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          loading={_QuerySmsReadFailed?.loading || isRefreshing}
        />
      </Box>
    </Box>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {
  title: PropTypes.string,
};

/*
 * EXPORTS
 */
export default Index;
